import React, { useEffect, useRef, useState } from "react";
import type { Role } from "../../domain/types/admin.types";

interface RoleSelectorProps {
  roles: Role[];
  selected: string[];
  onChange: (selected: string[]) => void;
  maxHeight?: string;
}

export const RoleSelector: React.FC<RoleSelectorProps> = ({
  roles,
  selected,
  onChange,
  maxHeight = "max-h-60",
}) => {
  const [search, setSearch] = useState("");
  const selectAllRef = useRef<HTMLInputElement>(null);

  const filtered = roles.filter((r) => {
    const q = search.toLowerCase();
    return r.name.toLowerCase().includes(q) || (r.description?.toLowerCase().includes(q) ?? false);
  });

  const allSelected = roles.length > 0 && roles.every((r) => selected.includes(r.name));
  const someSelected = !allSelected && roles.some((r) => selected.includes(r.name));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const toggleAll = () => {
    onChange(allSelected ? [] : roles.map((r) => r.name));
  };

  const toggle = (name: string) => {
    onChange(selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name]);
  };

  return (
    <div className="flex flex-col gap-2">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search roles…"
        className="h-9 w-full rounded border border-border-gray bg-white px-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-blue"
      />

      <label className="flex items-center gap-3 px-2 py-1.5 rounded border border-border-gray bg-default-99 cursor-pointer select-none">
        <input
          ref={selectAllRef}
          type="checkbox"
          className="h-4 w-4 flex-shrink-0 accent-primary-blue"
          checked={allSelected}
          onChange={toggleAll}
        />
        <span className="text-xs font-semibold text-gray-600">
          Select all
          {selected.length > 0 && (
            <span className="ml-1 text-gray-400 font-normal">({selected.length} selected)</span>
          )}
        </span>
      </label>

      <div className={`flex flex-col gap-0.5 overflow-y-auto ${maxHeight}`}>
        {filtered.length === 0 ? (
          <p className="px-2 py-3 text-sm text-center text-gray-400">No roles found.</p>
        ) : (
          filtered.map((role) => (
            <label
              key={role.id}
              className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-default-99 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                className="h-4 w-4 flex-shrink-0 accent-primary-blue"
                checked={selected.includes(role.name)}
                onChange={() => toggle(role.name)}
              />
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-gray-700 break-all">{role.name}</span>
                {role.description && (
                  <span className="text-xs text-gray-400">{role.description}</span>
                )}
              </div>
            </label>
          ))
        )}
      </div>
    </div>
  );
};
